import React, { useState, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import union from 'lodash/union'

import lang from '@/lang'

import SubscriptionItem, { bestsellerBadgeHeight } from '@/modules/base/components/modal/upgrade-modal/subscription-selection/subscription-item/subscription-item'
import { ButtonIconSquare } from '@/modules/base/components/buttons/index'
import CheckIcon from '@assets/images/icons/check.svg'
import getTexts from './_helpers/getTexts'
import Header from '@/modules/base/components/modal/upgrade-modal/header/header'
import PropTypes from 'prop-types'
import { ToggleSlider } from '@/modules/base/components/inputs'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ProductMainIcon from '@assets/images/product/main_blue_with_icon.svg'
import ProductDocumentIcon from '@assets/images/product/document_blue_with_icon.svg'
import ProductStaffIcon from '@assets/images/product/staff_blue_with_icon.svg'
import { ProductTypes, dateConstants } from '@/modules/base/util/constants'
import { useMediaQuery } from '@mui/material'
import ModuleItemList from '@/modules/moduleSubscription/components/list/module-item-list/module-item-list'
import { useAppSelector } from '@/redux/store'
import ModuleSelection from '@/modules/base/components/modal/upgrade-modal/module-selection/module-selection'
import LegacyPlanHint from './legacy-plan-hint/legacy-plan-hint'
import { isLegacyPlan } from './_helpers/isLegacyPlan'
import { isMeinBuero } from '@/utils/build-env'

const isMeinbuero = isMeinBuero()

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
    [theme.devices.mobile]: {
      padding: '0px 20px',
      gap: 0,
    },
  },
  oldPlanHint: {
    padding: '10px 50px 50px',
    textAlign: 'center',
  },
  header: {
    gridArea: 'header',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    flexWrap: 'wrap',
    [theme.breakpoints.down(1250)]: {
      alignItems: 'flex-end',
      marginRight: 40,
    },
    [theme.devices.mobile]: {
      alignItems: 'flex-start',
    },
  },
  subTitle: {
    marginTop: 20,
    fontSize: 14,
    color: theme.textLight,
  },
  periodSwitchWrapper: {
    display: 'flex',
    alignItems: 'center',
    fontSize: theme.fontSizes.smedium,
    gap: 15,
  },
  periodSwitch: {
    '& > .react-toggle-track': {
      width: 48,
      height: 24,
      backgroundColor: '#C5D5FE',
    },
    '& > .react-toggle-thumb': {
      top: 1,
      left: 1,
      height: 23,
      width: 23,
      background: theme.colorPrimaryButtonBackground,
    },
    '&.checked > .react-toggle-thumb': {
      left: 25,
    },
    '&.checked > .react-toggle-track': {
      backgroundColor: '#C5D5FE',
    },
    '&.checked:hover > .react-toggle-track': {
      backgroundColor: '#C5D5FE' + ' !important',
    },
    '&:hover > .react-toggle-track': {
      backgroundColor: '#C5D5FE' + ' !important',
    },
  },
  content: {
    gridArea: 'content',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: '0 auto',
    marginTop: 45,
    padding: '0px 40px',
    gap: 15,
    [theme.breakpoints.down(1400)]: {
      padding: 0,
    },
    [theme.breakpoints.down(1250)]: {
      padding: '0 40px',
    },
    [theme.breakpoints.down('md')]: {
      marginTop: 32,
      padding: 0,
      paddingBottom: 140,
    },
  },
  legacyPlanHint: ({ hasBestseller }) => ({
    width: 0,
    minWidth: '100%',
    marginBottom: hasBestseller ? bestsellerBadgeHeight : 0,
    [theme.breakpoints.down(1250)]: {
      marginBottom: 0,
    },
  }),
  items: {
    display: 'flex',
    gap: 32,
    paddingBottom: 10,
    width: '100%',
    flexWrap: 'wrap',
    justifyContent: 'center',
    [theme.breakpoints.down('lg')]: {
      justifyContent: 'center',
      gap: 16,
    },
    [theme.breakpoints.down(1400)]: {
      gap: 10,
    },
    [theme.breakpoints.down(1250)]: {
      gap: 20,
      alignItems: 'flex-start',
    },
  },
  hint: {
    textAlign: 'left',
    color: theme.textLight,
    fontSize: theme.fontSizes.smaller,
    alignSelf: 'flex-start',
  },
  hintCentered: {
    textAlign: 'center',
    alignSelf: 'center',
    color: theme.grey3,
    marginTop: 15,
    [theme.devices.mobile]: {
      marginTop: 10,
    },
  },
  continueButton: {
    marginLeft: 15,
    minWidth: 150,
  },
  noUpgradePlan: {
    display: 'block',
    paddingBottom: 10,
    width: '100%',
    textAlign: 'center',
  },
  moduleArea: {
    marginTop: 18,
  },
  moduleAreaTitle: {
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.medium,
  },
  freePlanContainer: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
    },
  },
}))

const subscriptionTypeIcon = {
  [ProductTypes.DOCUMENT]: ProductDocumentIcon,
  [ProductTypes.MAIN]: ProductMainIcon,
  [ProductTypes.STAFF]: ProductStaffIcon,
}

const SubscriptionSelection = (props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    isOldPlan,
    subscriptionType,
    overrideTexts,
    customClass = {},
    subscriptionTypes = [],
    hideSubtitle = false,
    preSelectedPeriod = dateConstants.YEAR,
    periodSwitchReadOnly = false,
    hidePeriodSwitch = false,
    shoppingCart,
    showAvailableModules = false,
    modules = [],
    onCancel,
    onSelect,
    onAddModuleToCart,
  } = props
  const [subscriptions, setSubscriptions] = useState([])
  const [selectedSubscription, setSelectedSubscription] = useState()
  const [hasBestseller, setHasBestseller] = useState(false)
  const [texts, setTexts] = useState({})
  const [selectedPeriod, setSelectedPeriod] = useState(preSelectedPeriod || dateConstants.YEAR)
  const [availablePeriod, setAvailablePeriod] = useState([])
  const classes = useStyles({ hasBestseller })
  const activeSubscriptions = useAppSelector(state => state.subscription.details.subscriptions)
  const activeSubscription = useMemo(() => {
    return activeSubscriptions.find(sub => sub.typeOfSubscription === subscriptionType)
  }, [activeSubscriptions, subscriptionType])

  const showLegacyPlanHint = !isMeinbuero && isLegacyPlan(activeSubscription, subscriptionTypes)

  const isItemSelectable = useMemo(() => {
    return !(shoppingCart?.items?.[subscriptionType] && Object.values(shoppingCart.items[subscriptionType]).some(item => item.type))
  }, [shoppingCart, subscriptionType])

  useEffect(() => {
    if (!preSelectedPeriod) { return }
    setSelectedPeriod?.(preSelectedPeriod)
  }, [preSelectedPeriod])

  useEffect(() => {
    if (subscriptionTypes.length) {
      const periods = union(subscriptionTypes.flatMap(subscriptionType => subscriptionType.prices).flatMap(prices => prices.period))
      const bestseller = subscriptionTypes.find(type => type.bestseller)
      setAvailablePeriod(periods)
      setSelectedPeriod(periods.indexOf(selectedPeriod) >= 0 ? selectedPeriod : periods[0])
      setSubscriptions(subscriptionTypes)
      setHasBestseller(bestseller)
    }
  }, [subscriptionTypes])

  const changeSubscription = (subscription) => {
    setSelectedSubscription(subscription)
    setTexts({ ...getTexts(subscription.prices, subscription?.type), ...overrideTexts })
  }

  const periodSwitch = useMemo(() => {
    if (hidePeriodSwitch) return null
    return (
      <div className={classes.periodSwitchWrapper}>
        <span>{lang.timeIntervals.monthly}</span>
        <ToggleSlider
          className={classes.periodSwitch}
          checked={selectedPeriod === dateConstants.YEAR}
          name="selectedPeriod"
          readOnly={periodSwitchReadOnly}
          onChange={(enabled) => {
            setSelectedPeriod(enabled ? dateConstants.YEAR : dateConstants.MONTH)
          }} />
        <span>{lang.timeIntervals.yearly}</span>
      </div>
    )
  }, [selectedPeriod, periodSwitchReadOnly, hidePeriodSwitch, availablePeriod])

  // WIP: Right now not required, preparation for dynamic descriptions
  // See: subscription-item.tsx
  // const featureDiffs = useMemo(() => {
  //   return subscriptions?.reduce((result, sub, index) => {
  //     const { features } = sub
  //     result[sub.name] = {
  //       new: Object.keys(result).reduce((innerResult, subName) => {
  //         const { features: featuresOfSub } = subscriptions.find(sub => sub.name === subName)
  //         return Object.keys(featuresOfSub).reduce((innerInnerResult, featureKey) => {
  //           if (features[featureKey]?.moduleEnabled && featuresOfSub[featureKey]?.moduleEnabled === false) {
  //             innerInnerResult.push(featureKey)
  //           } else if (features[featureKey]?.includedCount > featuresOfSub[featureKey]?.includedCount) {
  //             innerInnerResult.push(featureKey)
  //           } else if (features[featureKey]?.includedCount <= featuresOfSub[featureKey]?.includedCount) {
  //             innerInnerResult = innerInnerResult.filter(val => val !== featureKey)
  //           } else if (features[featureKey]?.moduleEnabled && featuresOfSub[featureKey]?.moduleEnabled) {
  //             innerInnerResult = innerInnerResult.filter(val => val !== featureKey)
  //           }
  //           return innerInnerResult
  //         }, [])
  //       }, []),
  //     }
  //     return result
  //   }, {})
  // }, [subscriptions])

  const items = useMemo(() => {
    if (!subscriptions?.length) {
      return <div className={classes.noUpgradePlan} dangerouslySetInnerHTML={{ __html: lang.upgrade.wizard.selection.noUpgradeAvailable }} />
    }

    return subscriptions
      .reduce((items, subscription, index) => {
        const inCart = shoppingCart?.items?.[subscription.type] && Object.keys(shoppingCart?.items?.[subscription.type]).some(name => name === subscription?.name)
        const isActive = activeSubscription?.type === subscription?.name
        const isFreeSubscription = subscription.name.includes('free_')
        const horizontal = !isMobile && isFreeSubscription

        const item = (
          <SubscriptionItem
            key={index}
            period={selectedPeriod}
            active={isActive}
            inCart={inCart}
            selectable={isItemSelectable}
            subscription={subscription}
            activeSubscription={activeSubscription}
            // WIP: Right now not required, preparation for dynamic descriptions
            // featureDiffs={featureDiffs}
            onClick={changeSubscription}
            onSelect={(sub) => onSelect(sub, selectedPeriod)}
            horizontal={horizontal}
          />
        )

        if (subscription.name.includes('free_')) {
          items.freeSubscription = item
        } else {
          items.subscriptions.push(item)
        }

        return items
      }, { subscriptions: [], freeSubscription: null })
  }, [isMobile, subscriptions, selectedPeriod, selectedSubscription, shoppingCart, isItemSelectable, activeSubscription])

  if (isOldPlan) {
    return (
      <div className={classes.container}>
        <Header title={lang.subscriptionDetails.changeSubscription } />
        <div className={classes.oldPlanHint} dangerouslySetInnerHTML={{ __html: lang.upgrade.wizard.selection.oldPlanHint }}></div>
        <ButtonIconSquare
          label={lang.generics.understood}
          icon={CheckIcon}
          customClass={classes.continueButton}
          onClick={onCancel} />
      </div>
    )
  }

  const availableModules = useMemo(() => {
    if (!showAvailableModules || !modules?.length) { return null }

    const title = <div className={classes.moduleAreaTitle}>{lang.moduleItemList.title}</div>
    return (
      <div className={classes.moduleArea}>
        {title}
        {activeSubscription
          ? (
            <ModuleSelection
              hideFooter
              hideLogo
              disableScroll
              activeSubscriptions={activeSubscriptions}
              subscriptionType={subscriptionType}
              selectedPeriod={selectedPeriod}
              items={shoppingCart?.items}
              onCancel={onCancel}
              onChangeShoppingCart={onAddModuleToCart}
            />
          )
          : (
            <ModuleItemList subscriptionType={subscriptionType} selectedPeriod={selectedPeriod} modules={modules} />
          )
        }
      </div>
    )
  }, [shoppingCart, modules, showAvailableModules, activeSubscription])

  const { subTitle } = texts
  const hint = texts?.hints?.[selectedPeriod] || (selectedPeriod === dateConstants.YEAR ? lang.subscriptionSelectionHintYear : lang.subscriptionSelectionHintMonth)

  return (
    <div className={classes.container}>
      <div className={clsx(classes.header, customClass.header)}>
        {!!subTitle && !hideSubtitle && <SVGIcon className={classes.subTitle} svg={subscriptionTypeIcon[subscriptionType]} height='30px' width='auto' />}
        {periodSwitch}
      </div>
      <div className={classes.content}>
        {showLegacyPlanHint && <div className={classes.legacyPlanHint}><LegacyPlanHint /></div>}
        <div className={clsx(classes.items, customClass.items)}>
          {items.subscriptions}
        </div>
        {items.freeSubscription && (
          <div className={classes.freePlanContainer}>
            {items.freeSubscription}
          </div>
        )}
        {availableModules}
        <span className={clsx(classes.hint, classes.hintCentered)}>{hint}</span>
      </div>
    </div>
  )
}

SubscriptionSelection.propTypes = {
  customClass: PropTypes.object,
  readOnly: PropTypes.bool,
  hideSubtitle: PropTypes.bool,
  subscriptionTypes: PropTypes.array,
  trialExpired: PropTypes.bool,
  subscriptionType: PropTypes.string,
  periodSwitchReadOnly: PropTypes.bool,
  preSelectedPeriod: PropTypes.string,
  hidePeriodSwitch: PropTypes.bool,
  onSelect: PropTypes.func,
  onCancel: PropTypes.func,
  onSkip: PropTypes.func,
  selectedSubscription: PropTypes.object,
  shoppingCart: PropTypes.object,
  showAvailableModules: PropTypes.bool,
  modules: PropTypes.array,
  onAddModuleToCart: PropTypes.func,
}

export default SubscriptionSelection
